import { ru, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import { buttonVariants } from '@/ui/Button';
import { cn } from '@/utils';
import { useTranslation } from 'react-i18next';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const localeObject = {
	'ru': ru,
	'en': enUS
}

const IconLeft: React.FC = (props) => <ChevronLeft {...props} className="h-4 w-4" />;
const IconRight: React.FC = (props) => <ChevronRight {...props} className="h-4 w-4" />;

function Calendar(props: CalendarProps) {
	const { i18n } = useTranslation()
	const { className, classNames, showOutsideDays = true, ...restProps } = props;

	const locale = React.useMemo(() => {
		return localeObject[i18n.language];
	}, [i18n.language])

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn('p-3', className)}
			locale={locale}
			classNames={{
				months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full',
				month: 'space-y-4 w-full',
				caption: 'flex justify-center pt-1 relative items-center',
				caption_label: 'text-sm font-medium',
				nav: 'space-x-1 flex items-center',
				nav_button: cn(
					buttonVariants({ variant: 'tetriary' }),
					'border border-brand-gray-4 bg-background shadow-sm hover:bg-brand-blue hover:text-brand-gray-4',
					'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
				),
				nav_button_previous: 'absolute left-1',
				nav_button_next: 'absolute left-1',
				table: 'w-full border-collapse space-y-1',
				head_row: 'flex justify-between px-2.5 max-w-[252px]',
				head_cell: 'text-brand-gray-1 rounded-md font-normal text-13',
				row: 'flex w-full mt-2',
				cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-brand-blue/50 [&:has([aria-selected].day-outside)]:rounded-md [&:has([aria-selected])]:bg-brand-brand-blue first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
				day: cn(buttonVariants({ variant: 'ghost' }), 'h-9 w-9 p-0 font-normal aria-selected:opacity-100'),
				day_range_end: 'day-range-end',
				day_selected:
					'bg-brand-blue text-slate-50 hover:bg-brand-blue hover:text-slate-50 focus:bg-brand-blue focus:text-slate-50',
				day_today: 'bg-brand-gray-4 text-brand-dark aria-selected:bg-brand-blue aria-selected:text-white',
				day_outside:
					'day-outside text-brand-gray-2 opacity-50 aria-selected:bg-brand-gray-5/50 aria-selected:text-brand-gray-4 aria-selected:opacity-30',
				day_disabled: 'text-brand-gray-4 opacity-50',
				day_range_middle: 'aria-selected:bg-gray-100 aria-selected:text-gray-800',
				day_hidden: 'invisible',
				...classNames,
			}}
			components={{
				IconLeft,
				IconRight,
			}}
			{...restProps}
		/>
	);
}
Calendar.displayName = 'Calendar';

export { Calendar };
